import moment from 'lib/moment';

import {
  LessonPlanGroup,
  LessonPlanItem,
  LessonPlanMilestone,
  VisibilitySetting,
} from '../types';

/**
 * Adds a new attribute itemTypeKey to the lesson plan item.
 * itemTypeKey has two functions:
 * 1. It serves as key for the visibilityByType hash
 * 2. It is used as the display string for the 'type' of the item.
 */
export function generateTypeKey(item: LessonPlanItem): LessonPlanItem {
  return {
    ...item,
    itemTypeKey: (item.lesson_plan_item_type ?? []).join(': '),
  };
}

/**
 * Converts the visibility settings received from the backend into a hash keyed by
 * itemTypeKey (as generated by generateTypeKey above).
 *
 * Example:
 *
 * Each setting is a hash like { setting_key: ['Standard Assessment', 'Tab 2'], visible: false }.
 *
 * This becomes the visibilitySetting hash { 'Standard Assessment: Tab 2': false } where the key
 * is in the same format as itemTypeKey.
 */
export function generateVisibilitySettings(
  visibilitySettings: VisibilitySetting[],
): Record<string, boolean> {
  const newVisibilitySettings: Record<string, boolean> = {};
  visibilitySettings.forEach((setting) => {
    newVisibilitySettings[setting.setting_key.join(': ')] = setting.visible;
  });
  return newVisibilitySettings;
}

function sortByStartAt(
  a: { start_at?: LessonPlanItem['start_at'] },
  b: { start_at?: LessonPlanItem['start_at'] },
): number {
  const aStartAt = moment(a.start_at);
  if (aStartAt.isAfter(b.start_at)) {
    return 1;
  }
  if (aStartAt.isBefore(b.start_at)) {
    return -1;
  }
  return 0;
}

/**
 * Groups lesson plan items under the milestone they fall after. Items before the
 * first milestone are grouped on their own.
 */
export function groupItemsUnderMilestones(
  items: LessonPlanItem[],
  milestones: LessonPlanMilestone[],
): LessonPlanGroup[] {
  const sortedMilestones = [...milestones].sort(sortByStartAt);
  const sortedItems = [...items].sort((a, b) => {
    const startAtSortResult = sortByStartAt(a, b);
    if (startAtSortResult !== 0) {
      return startAtSortResult;
    }
    const itemTypeSortResult = (a.itemTypeKey ?? '').localeCompare(
      b.itemTypeKey ?? '',
    );
    if (itemTypeSortResult !== 0) {
      return itemTypeSortResult;
    }
    return a.title.localeCompare(b.title);
  });

  const groups: LessonPlanGroup[] = [];
  let milestone: LessonPlanMilestone | null = null;
  let groupItems: LessonPlanItem[] = [];

  // Adds the current group to groups and resets it
  const addGroup = (): void => {
    if (groupItems.length > 0 || milestone) {
      groups.push({
        id: `milestone-group-${milestone ? milestone.id : 'ungrouped'}`,
        milestone,
        items: groupItems,
      });

      milestone = null;
      groupItems = [];
    }
  };

  sortedMilestones.forEach((nextMilestone) => {
    // Group items that come before the current milestone under the previous milestone
    while (
      sortedItems.length > 0 &&
      moment(sortedItems[0].start_at).isBefore(nextMilestone.start_at)
    ) {
      groupItems.push(sortedItems.shift() as LessonPlanItem);
    }
    // Finalize the group, then start a new group with the current milestone
    addGroup();
    milestone = nextMilestone;
  });
  // The remaining items belong with the last milestone
  groupItems = groupItems.concat(sortedItems);
  addGroup();

  return groups;
}

/**
 * Generates a hash that indicates the visibility of each item type, e.g. :
 *   { "Training: Extra": false, "Recitation": true }
 * as read from the given visibilitySettings.
 *
 * All other items are visible by default.
 */
export function initializeVisibility(
  items: LessonPlanItem[],
  visibilitySettings: Record<string, boolean>,
): Record<string, boolean> {
  const itemTypes = new Set(items.map((item) => item.itemTypeKey ?? ''));
  const visibility: Record<string, boolean> = {};
  itemTypes.forEach((itemType) => {
    const hasVisibilitySetting = Object.prototype.hasOwnProperty.call(
      visibilitySettings,
      itemType,
    );
    visibility[itemType] = hasVisibilitySetting
      ? visibilitySettings[itemType]
      : true;
  });
  return visibility;
}
